<script lang="ts">
	/**
	 * The headline figures and the funnel (§12.10) — the two things somebody opening this page came
	 * for, in the order they ask for them: how did we do, and where are we losing people.
	 *
	 * **Every figure is against the previous period of equal length.** A number with no comparison
	 * is not information: "1,412 visits" is a good month or a catastrophe depending entirely on
	 * what last month was, and a dashboard that makes the reader remember is a dashboard nobody
	 * checks twice.
	 *
	 * Money goes through `CurrencyFigure`, one line per currency and **no total** — minor units in
	 * two currencies are different units, and a summed figure is the one shape here that would be
	 * wrong in a way nobody could see.
	 *
	 * `unattributedPurchases` is **stated rather than hidden**. A store that switched collection on
	 * last week has a year of sales attributable to nothing, and dropping them silently would make
	 * this page disagree with the store's own revenue with nothing on screen to explain it.
	 */

	import { EmptyState, Panel, CurrencyFigure } from '../../../components';
	import type { AnalyticsOverview } from '../../../AppTypes';
	import { analyticsRetentionNote } from '../../../Utilities';
	import StatTile from '../StatTile.svelte';
	import FunnelChart from '../../../components/charts/FunnelChart.svelte';

	export let overview: AnalyticsOverview | null = null;
	export let loading: boolean = false;
	export let error: string = '';

	/** A change against the previous period, as words rather than as a colour alone. */
	function fnDelta(current: number, previous: number): string {
		if (previous === 0) return current === 0 ? 'no change' : 'no comparison';
		const change = ((current - previous) / previous) * 100;
		const rounded = Math.abs(change) < 10 ? change.toFixed(1) : Math.round(change).toString();
		return `${change >= 0 ? '+' : ''}${rounded}% vs previous`;
	}

	/**
	 * A conversion rate, to one decimal — and to two below one per cent.
	 *
	 * Storefront conversion lives between about one and five per cent, so one decimal is the
	 * resolution a change is actually visible at. Below one per cent a single decimal rounds every
	 * distinct rate to the same figure, which reads as a metric that never moves.
	 */
	function fnPercent(fraction: number): string {
		return `${(fraction * 100).toFixed(fraction < 0.01 ? 2 : 1)}%`;
	}
</script>

<Panel title="Overview" faIcon="chart-line">
	<!--
		Visits and visitors past the cutoff cannot be recovered: the archive holds distinct sessions
		per day, and adding those across a range counts somebody who came back on Tuesday twice.
		Declared missing rather than approximated — a figure wrong in the flattering direction is the
		last one anybody questions. Revenue and purchases are unaffected; they come from the money
		tables, which retention never touches.
	-->
	{#if overview !== null && analyticsRetentionNote(overview.retention, false).length > 0}
		<p class="text-amber-600 dark:text-amber-400 text-xs mb-3">
			{analyticsRetentionNote(overview.retention, false)}
		</p>
	{/if}

	{#if error.length > 0}
		<EmptyState variant="error" title="The overview could not be loaded." description={error} />
	{:else if overview === null}
		<EmptyState variant="loading" />
	{:else}
		<div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
			<StatTile
				faIcon="users"
				tone="blue"
				label="Visitors"
				{loading}
				value={overview.visitors.toLocaleString()}
				detail={fnDelta(overview.visitors, overview.comparison.visitors)}
			/>
			<StatTile
				faIcon="clock-rotate-left"
				tone="sky"
				label="Visits"
				{loading}
				value={overview.sessions.toLocaleString()}
				detail={fnDelta(overview.sessions, overview.comparison.sessions)}
			/>
			<StatTile
				faIcon="cart-shopping"
				tone="green"
				label="Purchases"
				{loading}
				value={overview.purchases.toLocaleString()}
				detail={fnDelta(overview.purchases, overview.comparison.purchases)}
			/>
			<StatTile
				faIcon="percent"
				tone="purple"
				label="Visit-to-purchase"
				{loading}
				value={fnPercent(overview.conversionRate)}
				detail={`${overview.purchases.toLocaleString()} of ${overview.sessions.toLocaleString()} visits`}
			/>
		</div>

		<div class="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
			<div class="lg:col-span-2 widget bg-depth min-w-0">
				<div class="card-label mb-2"><i class="fa-solid fa-filter text-[11px] mr-1" />Funnel</div>
				<FunnelChart stages={overview.funnel} />
			</div>

			<div class="space-y-3 min-w-0">
				<CurrencyFigure
					label="First-purchase revenue in this range"
					faIcon="sack-dollar"
					amounts={overview.revenue}
					emptyText="No purchases completed in this range."
					description="What the purchases made in this range took at checkout — a subscription at its first paid invoice, renewals excluded, which is why it is lower than Reports and Operations. Never from an analytics row."
				/>
				<div class="widget bg-depth">
					<div class="card-label mb-2">
						<i class="fa-solid fa-eye text-[11px] mr-1" />Engagement
					</div>
					<dl class="text-sm space-y-1">
						<div class="flex justify-between gap-3">
							<dt class="text-default">Page views</dt>
							<dd class="tabular-nums font-medium">{overview.pageViews.toLocaleString()}</dd>
						</div>
						<div class="flex justify-between gap-3">
							<dt class="text-default">Product views</dt>
							<dd class="tabular-nums font-medium">{overview.productViews.toLocaleString()}</dd>
						</div>
						<div class="flex justify-between gap-3">
							<dt class="text-default">Visits that engaged the flow</dt>
							<dd class="tabular-nums font-medium">
								{overview.engagedSessions.toLocaleString()}
							</dd>
						</div>
						<div class="flex justify-between gap-3">
							<dt class="text-default">Visits that reached payment</dt>
							<dd class="tabular-nums font-medium">
								{overview.checkoutSessions.toLocaleString()}
							</dd>
						</div>
					</dl>
				</div>
			</div>
		</div>

		{#if overview.unattributedPurchases > 0}
			<!--
				Not an error. It is what a store that switched collection on after it started selling
				looks like, and it is worth a sentence rather than a silent discrepancy between this
				page and Operations.
			-->
			<p class="text-default text-xs mt-3">
				{overview.unattributedPurchases.toLocaleString()} completed
				{overview.unattributedPurchases === 1 ? 'purchase' : 'purchases'} in this range
				{overview.unattributedPurchases === 1 ? 'carries' : 'carry'} no visit behind
				{overview.unattributedPurchases === 1 ? 'it' : 'them'} — bought before collection was
				switched on, or with it off. They count toward revenue and toward nothing above it.
			</p>
		{/if}
	{/if}
</Panel>
